import type { ReactNode } from "react"

import { Box, Chip, Paper, Stack, Typography } from "@mui/material"

import { ACTOR_META, GuideActor, GuideSectionId } from "./guideContent"

export function ActorChip({
  actor,
  size = "small"
}: {
  actor: GuideActor
  size?: "small" | "medium"
}) {
  const meta = ACTOR_META[actor]
  return (
    <Chip
      size={size}
      label={meta.label}
      sx={{
        bgcolor: `${meta.color}1A`,
        color: meta.color,
        fontWeight: 800,
        border: `1px solid ${meta.color}55`
      }}
    />
  )
}

export function GuideSection({
  id,
  eyebrow,
  title,
  lead,
  children
}: {
  id: GuideSectionId
  eyebrow: string
  title: string
  lead: string
  children: ReactNode
}) {
  return (
    <Box component="section" id={`ai-guide-${id}`} sx={{ scrollMarginTop: 16 }}>
      <Typography
        variant="overline"
        sx={{ color: "primary.main", fontWeight: 800, letterSpacing: 1 }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "text.secondary", mb: 3, maxWidth: 760, lineHeight: 1.6 }}
      >
        {lead}
      </Typography>
      {children}
    </Box>
  )
}

export function Card({
  children,
  accent
}: {
  children: ReactNode
  accent?: string
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: 2,
        borderTop: accent ? `3px solid ${accent}` : undefined
      }}
    >
      {children}
    </Paper>
  )
}

export function BulletList({ items }: { items: string[] }) {
  return (
    <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
      {items.map((item) => (
        <Typography
          component="li"
          key={item}
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.55 }}
        >
          {item}
        </Typography>
      ))}
    </Stack>
  )
}

export function CodeLine({ children }: { children: string }) {
  return (
    <Box
      component="code"
      sx={{
        display: "block",
        px: 1,
        py: 0.75,
        bgcolor: "#F7F7FA",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        fontSize: 12,
        overflowWrap: "anywhere"
      }}
    >
      {children}
    </Box>
  )
}
